import { useState } from 'react'
import { useSupportDevMappings, useUpsertSupportDevMapping, useEffectiveAccess, type SupportDevMapping } from '@/api/queries'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'

/**
 * Spec 351 — Escalation admin page. Maps each support project (SS/VS/ST) to the development project
 * (SD/VD/WFT) its engineering escalations go to, plus a per-project on/off gate. The bridge reads this live and
 * only lets a support agent auto-create + link a dev ticket for a support project whose mapping is enabled
 * (ST→WFT during testing; SS/VS off until turned on). Mirrors AccessRoles.
 */
export function Escalation() {
  const q = useSupportDevMappings()
  const upsert = useUpsertSupportDevMapping()
  const me = useEffectiveAccess()
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState({ supportProjectKey: '', devProjectKey: '', escalationEnabled: true, clientNoteEnabled: false })
  const [err, setErr] = useState<string | null>(null)

  const canManage = (me.data?.capabilities ?? []).includes('access.manage')
  const items = q.data ?? []

  const edit = (m: SupportDevMapping) => {
    setForm({ supportProjectKey: m.supportProjectKey, devProjectKey: m.devProjectKey, escalationEnabled: m.escalationEnabled, clientNoteEnabled: m.clientNoteEnabled })
    setErr(null)
    setOpen(true)
  }

  const add = () => {
    setForm({ supportProjectKey: '', devProjectKey: '', escalationEnabled: true, clientNoteEnabled: false })
    setErr(null)
    setOpen(true)
  }

  const toggle = (m: SupportDevMapping) => {
    if (!canManage) return
    upsert.mutate({ supportProjectKey: m.supportProjectKey, devProjectKey: m.devProjectKey, escalationEnabled: !m.escalationEnabled, clientNoteEnabled: m.clientNoteEnabled })
  }

  // 357 — toggle the per-project client-facing "assigned to Development" note gate.
  const toggleClientNote = (m: SupportDevMapping) => {
    if (!canManage) return
    upsert.mutate({ supportProjectKey: m.supportProjectKey, devProjectKey: m.devProjectKey, escalationEnabled: m.escalationEnabled, clientNoteEnabled: !m.clientNoteEnabled })
  }

  const submit = () => {
    const sup = form.supportProjectKey.trim().toUpperCase()
    const dev = form.devProjectKey.trim().toUpperCase()
    if (!sup || !dev) return
    setErr(null)
    upsert.mutate(
      { supportProjectKey: sup, devProjectKey: dev, escalationEnabled: form.escalationEnabled, clientNoteEnabled: form.clientNoteEnabled },
      {
        onSuccess: () => setOpen(false),
        onError: (e: any) => setErr(e?.message ?? 'Could not save the mapping.'),
      },
    )
  }

  return (
    <div className="page">
      <div className="page-head">
        <div><h1>Escalation</h1><p>Support project → development project mapping. Escalation is auto-create + link, and runs only where enabled.</p></div>
        <Button variant="primary" disabled={!canManage} onClick={add} title={!canManage ? 'Administrator only' : ''}>+ Add mapping</Button>
      </div>

      <table className="tbl">
        <thead><tr><th>Support project</th><th>Dev project</th><th>Escalation</th><th>Client note</th><th>Updated by</th><th></th></tr></thead>
        <tbody>
          {items.map((m) => (
            <tr key={m.supportProjectKey}>
              <td className="mono">{m.supportProjectKey}</td>
              <td className="mono">{m.devProjectKey}</td>
              <td>
                <Badge tone={m.escalationEnabled ? 'ok' : 'neutral'}>{m.escalationEnabled ? 'Enabled' : 'Disabled'}</Badge>
              </td>
              <td>
                <Badge tone={m.clientNoteEnabled ? 'ok' : 'neutral'}>{m.clientNoteEnabled ? 'Enabled' : 'Disabled'}</Badge>
              </td>
              <td className="muted" style={{ fontSize: 'var(--fs-200)' }}>{m.updatedBy ?? '—'}</td>
              <td style={{ textAlign: 'right' }}>
                <div className="row" style={{ gap: 8, justifyContent: 'flex-end' }}>
                  <Button disabled={!canManage} onClick={() => toggle(m)} title={!canManage ? 'Administrator only' : ''}>
                    {m.escalationEnabled ? 'Disable' : 'Enable'}
                  </Button>
                  <Button disabled={!canManage} onClick={() => toggleClientNote(m)} title={!canManage ? 'Administrator only' : 'Toggle the client-facing note'}>
                    {m.clientNoteEnabled ? 'Client note off' : 'Client note on'}
                  </Button>
                  <Button disabled={!canManage} onClick={() => edit(m)} title={!canManage ? 'Administrator only' : ''}>Edit</Button>
                </div>
              </td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={6}><div className="empty">No escalation mappings yet.</div></td></tr>}
        </tbody>
      </table>

      {open && (
        <Modal title="Support → dev escalation mapping" onClose={() => setOpen(false)} actions={
          <>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="primary" busy={upsert.isPending} disabled={!form.supportProjectKey.trim() || !form.devProjectKey.trim()} onClick={submit}>Save mapping</Button>
          </>
        }>
          <div className="field">
            <label>Support project key <span className="req">*</span></label>
            <input className="input mono" value={form.supportProjectKey} onChange={(e) => setForm({ ...form, supportProjectKey: e.target.value })} placeholder="ST" />
          </div>
          <div className="field">
            <label>Development project key <span className="req">*</span></label>
            <input className="input mono" value={form.devProjectKey} onChange={(e) => setForm({ ...form, devProjectKey: e.target.value })} placeholder="WFT" />
          </div>
          <div className="field">
            <label className="row" style={{ gap: 8, alignItems: 'center' }}>
              <input type="checkbox" checked={form.escalationEnabled} onChange={(e) => setForm({ ...form, escalationEnabled: e.target.checked })} />
              Escalation enabled for this support project
            </label>
            <small className="muted">When on, a support agent may auto-create a linked Bug/Story/Service in the dev project for genuine engineering tickets.</small>
          </div>
          <div className="field">
            <label className="row" style={{ gap: 8, alignItems: 'center' }}>
              <input type="checkbox" checked={form.clientNoteEnabled} onChange={(e) => setForm({ ...form, clientNoteEnabled: e.target.checked })} />
              Client note on escalation
            </label>
            <small className="muted">When on, escalating a case to development also posts one customer-facing note on the case telling the client their issue is now with development. Turn on only after the client-facing wording is approved.</small>
          </div>
          {err && <div className="field"><Badge tone="err">{err}</Badge></div>}
        </Modal>
      )}
    </div>
  )
}
